import { Accept, DependencyFailedError, ForbiddenError, FromBody, FromQuery, HttpStatusCodes, Inject, InternalServerError, NotFoundError, Result, UnauthorizedError } from '@resourceful-hono/core';
import AuthResource from '../index.ts';
import { z } from 'zod';
import { encodeBase64Url, decodeBase64Url } from '@std/encoding/base64url';
import type { ExpectedAssertionResult } from 'fido2-lib';
import { WebAuthnCredential } from '../../../../entities/WebAuthnCredential.ts';
import { hash } from '../../../../common/utils.ts';
import { CHALLENGE_VALIDITY_PERIOD } from '../../../../services/OAuth/constants.ts';
import { POST_SCHEMA } from './schema.ts';
import { CustomContentTypes } from '../../../../common/enums.ts';
import { EnvironmentService } from '../../../../services/EnvironmentService.ts';
import { User } from '../../../../entities/User.ts';
import { IsNull, Not } from 'typeorm';

export class AssertionResource extends AuthResource {
  @Inject()
  declare private readonly env: EnvironmentService;

  public async PUT(@FromQuery('email', z.string().email()) email: string) {
    const user = await User.findOneBy({ email });
    if (!user) throw new NotFoundError(`User with email: ${email} not found`);
    const options = await this.webAuthn.assertionOptions();
    const credentials = await WebAuthnCredential.find({
      where: {
        user: { id: user.id },
        authenticatorCredentialId: Not(IsNull()),
      },
    });

    const allowCredentials = credentials
      .map<{ id: BufferSource, type: 'public-key' }>(credential => {
        return {
          id: decodeBase64Url(credential.authenticatorCredentialId ?? ''),
          type: 'public-key',
        };
      });
    if (!allowCredentials.length)
      throw new DependencyFailedError(`No credential found for user ${email}.`);

    const challenge = encodeBase64Url(options.challenge);
    await this.saveChallenges(credentials, challenge);
    
    return Result(HttpStatusCodes.Created, {
      publicKey: {
        ...options,
        userVerification: 'required' as const,
        allowCredentials,
      },
    }, CustomContentTypes.Cbor);
  }

  @Accept([CustomContentTypes.Cbor])
  public async POST(
    @FromBody(POST_SCHEMA) clientAssertionResponse: z.infer<typeof POST_SCHEMA>
  ) {
    const authenticatorCredentialId =
      encodeBase64Url(clientAssertionResponse.rawId);
    const credential = await WebAuthnCredential.findOne({
      where: {
        authenticatorCredentialId,
      },
      relations: { user: true },
    });
    if (!credential)
      throw new InternalServerError('Credential not found.');
    if (!credential.challenge)
      throw new DependencyFailedError('Credential challenge not found.');
    if (!credential.publicKey)
      throw new DependencyFailedError('Public key not found.');
    if (credential.challengeExpires && credential.challengeExpires < new Date())
      throw new ForbiddenError('Credential challenge expired.');
    const assertionExpectations: ExpectedAssertionResult = {
      challenge: credential.challenge,
      publicKey: credential.publicKey,
      origin: this.env.get('CLIENT_ORIGIN'),
      factor: 'first',
      prevCounter: credential.prevCounter,
      userHandle: null,
    };
    try {
      const { rawId, response } = clientAssertionResponse;
      const assertionResult = await this.webAuthn.assertionResult(
        {
          rawId: rawId.buffer,
          response: {
            authenticatorData: response.authenticatorData.slice().buffer,
            clientDataJSON: encodeBase64Url(response.clientDataJSON),
            signature: encodeBase64Url(response.signature),
          },
        },
        assertionExpectations
      );

      const counter = Number(assertionResult.authnrData.get('counter'));
      await this.updateCredential(credential.id, counter);
      const authnTokenResponse = await this.oAuth.generateAuthnToken(
        credential.user.id,
        credential.id
      );
      return Result(HttpStatusCodes.Ok, authnTokenResponse);
    } catch (cause) {
      throw new UnauthorizedError('Invalid assertion', { cause });
    }
  }

  private async updateCredential(
    webAuthnCredentialId: number,
    counter: number
  ) {
    const webAuthnCredential = await WebAuthnCredential.findOneBy({
      id: webAuthnCredentialId,
    });

    if (!webAuthnCredential) return;
    webAuthnCredential.prevCounter = counter;
    webAuthnCredential.challenge = undefined;
    webAuthnCredential.challengeHash = undefined;
    await webAuthnCredential.save();
  }

  private async saveChallenges(
    webAuthnCredentials: WebAuthnCredential[],
    challenge: string
  ) {
    return await Promise.all(
      webAuthnCredentials.map(async (webAuthnCredential) => {
        const challengeHash = await hash(challenge);
        const challengeExpires = new Date(
          Date.now() + CHALLENGE_VALIDITY_PERIOD
        );
  
        webAuthnCredential.challenge = challenge;
        webAuthnCredential.challengeHash = challengeHash;
        webAuthnCredential.challengeExpires = challengeExpires;
        return webAuthnCredential.save();
      })
    );
  }
}